import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Grid, Card, CardContent, Typography,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Modal, Fade,
  Backdrop, CircularProgress, Chip, IconButton,
  TextField, InputAdornment, Avatar, Tooltip, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SecurityIcon from '@mui/icons-material/Security';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SyncIcon from '@mui/icons-material/Sync';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '85%', md: '840px' },
  maxHeight: '85vh',
  overflowY: 'auto',
  bgcolor: '#ffffff',
  borderRadius: 3,
  boxShadow: '0 24px 48px rgba(15, 23, 42, 0.25)',
  p: { xs: 2.5, md: 3.5 },
  outline: 'none'
};

const getSiteNameFromDeviceId = (deviceId) => {
  if (!deviceId) return 'UNKNOWN';
  const parts = deviceId.split('-');
  return parts.length >= 3 ? parts[2].toUpperCase() : deviceId.toUpperCase();
};

const isValidInPunch = (log) => {
  if (!log) return false;
  const rawDirection = String(
    log.status || log.punchType || log.type || log.direction || log.logType || ''
  ).trim().toLowerCase();

  if (['out', 'checkout', 'check-out', '1', 'exit'].includes(rawDirection)) {
    return false;
  }
  return true;
};

// Formats timestamp cleanly into readable date time
const formatLastSeen = (timestamp) => {
  if (!timestamp) return 'Never Seen';
  try {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return String(timestamp);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return String(timestamp);
  }
};

// Universal CSV/Excel Downloader without external package dependency
const exportToCSV = (data, filename) => {
  if (!data || !data.length) {
    alert("No records available to export.");
    return;
  }
  const headers = Object.keys(data[0]);
  const csvRows = [];
  csvRows.push(headers.join(','));

  data.forEach((row) => {
    const values = headers.map((header) => {
      const escaped = ('' + (row[header] || '')).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(','));
  });

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Dashboard = () => {
  const [isPowerUser, setIsPowerUser] = useState(false);
  const [roleChecked, setRoleChecked] = useState(false);

  const [loading, setLoading] = useState(true);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [pullingSite, setPullingSite] = useState({});
  const [forceSyncing, setForceSyncing] = useState({});
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const [devices, setDevices] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});
  const [sitePunches, setSitePunches] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedSite, setSelectedSite] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalSearch, setModalSearch] = useState('');

  // 1. Role Verification Gate
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsPowerUser(false);
        setRoleChecked(true);
        return;
      }

      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      const userRole = String(decoded.role || decoded.userRole || '').trim().toLowerCase();
      const empCode = String(decoded.empCode || decoded.id || '').trim();

      const permitted =
        userRole === 'poweruser' ||
        userRole === 'power user' ||
        decoded.isPowerUser === true ||
        empCode === '115061';

      setIsPowerUser(permitted);
    } catch (err) {
      console.error("Token decoding error:", err);
      setIsPowerUser(true);
    } finally {
      setRoleChecked(true);
    }
  }, []);

  // 2. Fetch Cached Local Logs from Backend
  const readAllLocalLogs = useCallback(async (deviceList) => {
    const siteMap = {};

    await Promise.allSettled(
      deviceList.map(async (dev) => {
        const site = getSiteNameFromDeviceId(dev.deviceId);
        if (!siteMap[site]) siteMap[site] = [];

        try {
          const res = await axios.get(`https://gannondunkerley.in/devicelogs/view-local/${dev.deviceId}`);
          const rawLogs = res.data.logs || res.data || [];

          rawLogs.forEach((log) => {
            const code = String(log.empCode || log.employeeCode || log.userId || '').trim();
            if (code && isValidInPunch(log)) {
              if (!siteMap[site].some((item) => item.empCode === code)) {
                siteMap[site].push({
                  empCode: code,
                  name: log.name || '',
                  timestamp: log.timestamp || log.time || log.dateTime || 'Today',
                  status: log.status || 'In',
                  deviceId: dev.deviceId
                });
              }
            }
          });
        } catch {
          // Resilient bypass
        }
      })
    );

    setSitePunches((prev) => ({ ...prev, ...siteMap }));
    setLastSyncTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
  }, []);

  // 3. Automated Background Sync Loop
  const runAutoSync = useCallback(async (deviceList) => {
    if (!deviceList || deviceList.length === 0) return;
    setAutoSyncing(true);

    try {
      const onlineDevices = deviceList.filter((d) => d.status === 'Online');

      await Promise.allSettled(
        onlineDevices.map((dev) =>
          axios.post(
            'https://gannondunkerley.in/devicelogs/send-command',
            { deviceId: dev.deviceId, type: 'showLogs' },
            { timeout: 4000 }
          ).catch(() => null)
        )
      );

      await new Promise((resolve) => setTimeout(resolve, 3500));
      await readAllLocalLogs(deviceList);
    } catch (err) {
      console.error("Auto sync execution error:", err);
    } finally {
      setAutoSyncing(false);
    }
  }, [readAllLocalLogs]);

  // 4. Force Single Site Pull Trigger (Current Live Logs)
  const handleForcePullSite = async (siteName, deviceId) => {
    if (!deviceId) return;
    setPullingSite((prev) => ({ ...prev, [siteName]: true }));

    try {
      await axios.post('https://gannondunkerley.in/devicelogs/send-command', {
        deviceId,
        type: 'showLogs'
      });

      let attempts = 0;
      let logs = [];
      while (attempts < 6) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await axios.get(`https://gannondunkerley.in/devicelogs/view-local/${deviceId}`);
        logs = res.data.logs || res.data || [];
        if (logs.length > 0) break;
        attempts++;
      }

      if (logs.length > 0) {
        setSitePunches((prev) => {
          const updated = { ...prev };
          const list = [];
          logs.forEach((l) => {
            const code = String(l.empCode || l.employeeCode || l.userId || '').trim();
            if (code && isValidInPunch(l)) {
              if (!list.some((item) => item.empCode === code)) {
                list.push({
                  empCode: code,
                  name: l.name || '',
                  timestamp: l.timestamp || l.time || l.dateTime || 'Today',
                  status: l.status || 'In',
                  deviceId
                });
              }
            }
          });
          updated[siteName] = list;
          return updated;
        });
      } else {
        alert(`No new logs received from ${deviceId}. Device might be completing sync.`);
      }
    } catch (err) {
      console.error("Pull site error:", err);
      alert("Failed to communicate with device.");
    } finally {
      setPullingSite((prev) => ({ ...prev, [siteName]: false }));
    }
  };

  // 5. Force Sync Trigger (Historical / Past Pending Logs to Server)
  const handleForceSyncDevice = async (siteName, deviceId) => {
    if (!deviceId) return;
    setForceSyncing((prev) => ({ ...prev, [siteName]: true }));
    try {
      const res = await axios.post('https://gannondunkerley.in/devicelogs/device/trigger', { deviceId });
      alert(`[${siteName}] ${res.data.message || 'Force sync trigger signal sent to device!'}`);
      // Refresh status after trigger
      const statusRes = await axios.get('https://gannondunkerley.in/devicelogs/devices/status');
      setDevices(statusRes.data || []);
    } catch (err) {
      console.error("Force sync error:", err);
      alert(`Failed to trigger force sync for ${deviceId}`);
    } finally {
      setForceSyncing((prev) => ({ ...prev, [siteName]: false }));
    }
  };

  // 6. Initial Dashboard Bootstrap
  const fetchDashboardData = useCallback(async () => {
    try {
      const [devRes, empRes] = await Promise.allSettled([
        axios.get('https://gannondunkerley.in/devicelogs/devices/status', { timeout: 6000 }),
        axios.get('https://gannondunkerley.in/employees', { timeout: 6000 })
      ]);

      const deviceList = devRes.status === 'fulfilled' ? devRes.value.data || [] : [];
      setDevices(deviceList);

      const empData = empRes.status === 'fulfilled' ? (empRes.value.data.EmployeeList || empRes.value.data || []) : [];
      const map = {};
      empData.forEach((emp) => {
        if (emp.empCode) {
          map[String(emp.empCode).trim()] = {
            name: emp.name || emp.empName || 'Staff Member',
            projectCode: emp.projectCode || 'N/A',
            department: emp.category || 'Employee'
          };
        }
      });
      setEmployeeMap(map);

      setLoading(false);

      await readAllLocalLogs(deviceList);
      runAutoSync(deviceList);
    } catch (err) {
      console.error('Error fetching dashboard base data:', err);
      setLoading(false);
    }
  }, [readAllLocalLogs, runAutoSync]);

  useEffect(() => {
    if (isPowerUser) {
      fetchDashboardData();
      const syncInterval = setInterval(() => {
        fetchDashboardData();
      }, 35000);
      return () => clearInterval(syncInterval);
    }
  }, [isPowerUser, fetchDashboardData]);

  // Aggregate project statistics
  const siteCards = useMemo(() => {
    const map = {};
    devices.forEach((dev) => {
      const site = getSiteNameFromDeviceId(dev.deviceId);
      if (!map[site]) {
        map[site] = {
          siteName: site,
          devices: [],
          status: 'Offline',
          lastSeen: dev.lastSeen,
          trigger: dev.trigger
        };
      }
      map[site].devices.push(dev);
      if (dev.status === 'Online') map[site].status = 'Online';
      if (dev.lastSeen && (!map[site].lastSeen || new Date(dev.lastSeen) > new Date(map[site].lastSeen))) {
        map[site].lastSeen = dev.lastSeen;
      }
    });
    return Object.values(map);
  }, [devices]);

  const filteredSites = useMemo(() => {
    return siteCards.filter((site) =>
      site.siteName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.devices.some((d) => d.deviceId.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [siteCards, searchQuery]);

  const stats = useMemo(() => {
    const totalSites = siteCards.length;
    const onlineTerminals = siteCards.filter((s) => s.status === 'Online').length;
    const totalPresentHeadcount = Object.values(sitePunches).reduce((sum, list) => sum + list.length, 0);
    const activeSitesWithPunches = Object.values(sitePunches).filter((list) => list.length > 0).length;
    return { totalSites, onlineTerminals, totalPresentHeadcount, activeSitesWithPunches };
  }, [siteCards, sitePunches]);

  const handleOpenDrilldown = (siteName) => {
    setSelectedSite(siteName);
    setModalSearch('');
    setOpenModal(true);
  };

  const modalFilteredEmployees = useMemo(() => {
    if (!selectedSite || !sitePunches[selectedSite]) return [];
    return sitePunches[selectedSite].filter((emp) => {
      const details = employeeMap[String(emp.empCode).trim()];
      const name = emp.name || details?.name || '';
      return (
        String(emp.empCode).toLowerCase().includes(modalSearch.toLowerCase()) ||
        name.toLowerCase().includes(modalSearch.toLowerCase())
      );
    });
  }, [selectedSite, sitePunches, employeeMap, modalSearch]);

  // Export Specific Site
  const handleExportSiteCSV = () => {
    if (!selectedSite || !sitePunches[selectedSite] || sitePunches[selectedSite].length === 0) {
      alert("No punch records found to export for this site.");
      return;
    }
    const exportData = sitePunches[selectedSite].map((record, index) => {
      const empDetails = employeeMap[String(record.empCode).trim()];
      return {
        'SL No': index + 1,
        'Site Code': selectedSite,
        'Employee Code': record.empCode,
        'Employee Name': record.name || empDetails?.name || 'Staff Member',
        'Punch Time': record.timestamp,
        'Terminal ID': record.deviceId,
        'Direction': 'IN',
        'Status': 'Present'
      };
    });
    const dateStr = new Date().toISOString().split('T')[0];
    exportToCSV(exportData, `GDCL_${selectedSite}_Attendance_${dateStr}`);
  };

  // Export All Sites Globally
  const handleExportGlobalCSV = () => {
    const allRecords = [];
    let counter = 1;

    Object.keys(sitePunches).forEach((site) => {
      (sitePunches[site] || []).forEach((record) => {
        const empDetails = employeeMap[String(record.empCode).trim()];
        allRecords.push({
          'SL No': counter++,
          'Site Code': site,
          'Employee Code': record.empCode,
          'Employee Name': record.name || empDetails?.name || 'Staff Member',
          'Punch Time': record.timestamp,
          'Terminal ID': record.deviceId,
          'Direction': 'IN',
          'Status': 'Present'
        });
      });
    });

    if (allRecords.length === 0) {
      alert("No attendance records currently available across any site.");
      return;
    }

    const dateStr = new Date().toISOString().split('T')[0];
    exportToCSV(allRecords, `GDCL_Consolidated_Live_Attendance_${dateStr}`);
  };

  if (!roleChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={36} thickness={4} />
      </Box>
    );
  }

  if (!isPowerUser) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 480, border: '1px solid #fee2e2', bgcolor: '#fff5f5', borderRadius: 4 }}>
          <Avatar sx={{ bgcolor: '#ef4444', width: 56, height: 56, mx: 'auto', mb: 2 }}>
            <SecurityIcon fontSize="large" />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#991b1b', mb: 1 }}>
            Restricted Enterprise View
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
            Access to the Real-Time Project Attendance Dashboard is restricted exclusively to authorized <strong>Power Users</strong>.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="content" sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f6fa', minHeight: '100vh' }}>
      {/* Top Navigation & Global Controls */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
            GDCL Live Site Attendance
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Enterprise Headcount Analytics & Live Terminal Synchronization
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          {/* Global CSV/Excel Export */}
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportGlobalCSV}
            sx={{
              bgcolor: '#0f766e',
              '&:hover': { bgcolor: '#115e59' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 2,
              py: 0.75,
              boxShadow: '0 2px 4px rgba(15, 118, 110, 0.2)'
            }}
          >
            Export All Sites
          </Button>

          {/* Sync status indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ffffff', px: 2, py: 0.75, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: autoSyncing ? '#f59e0b' : '#10b981' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
              {autoSyncing ? 'Auto-Syncing...' : lastSyncTime ? `Synced at ${lastSyncTime}` : 'Monitoring Sites'}
            </Typography>
          </Box>

          <Tooltip title="Trigger Immediate Re-sync">
            <span>
              <IconButton onClick={fetchDashboardData} disabled={loading || autoSyncing} sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' }, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <RefreshIcon fontSize="small" sx={{ color: '#0284c7' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Metric Cards Banner */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {[
          { label: 'Total Sites', value: stats.totalSites, icon: <BusinessIcon />, color: '#0284c7', bg: '#e0f2fe' },
          { label: 'Online Terminals', value: `${stats.onlineTerminals} / ${stats.totalSites}`, icon: <WifiIcon />, color: '#16a34a', bg: '#dcfce7' },
          { label: 'Live Present Today (IN)', value: stats.totalPresentHeadcount, icon: <HowToRegIcon />, color: '#7c3aed', bg: '#ede9fe' },
          { label: 'Active Reporting Units', value: stats.activeSitesWithPunches, icon: <AccessTimeIcon />, color: '#ea580c', bg: '#ffedd5' }
        ].map((metric, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {metric.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                  {metric.value}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: metric.bg, color: metric.color, width: 44, height: 44, borderRadius: 2 }}>
                {metric.icon}
              </Avatar>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Search and Site Filter Bar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
        <TextField
          size="small"
          placeholder="Search by Site code or Device ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: '100%', sm: 360 }, bgcolor: '#ffffff', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Project Site Cards Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filteredSites.map((site) => {
            const punches = sitePunches[site.siteName] || [];
            const presentCount = punches.length;
            const primaryDev = site.devices[0];
            const isOnline = site.status === 'Online';
            const isPulling = Boolean(pullingSite[site.siteName]);
            const isForceSyncing = Boolean(forceSyncing[site.siteName]);

            return (
              <Grid item xs={12} sm={6} md={4} key={site.siteName}>
                <Card
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#ffffff',
                    border: `1px solid ${isOnline ? '#e2e8f0' : '#fed7aa'}`, // Subtle alert accent on offline cards
                    boxShadow: isOnline 
                      ? '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -2px rgba(0,0,0,0.02)' 
                      : '0 2px 8px rgba(234, 88, 12, 0.05)',
                    transition: 'all 0.25s ease-in-out',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px -6px rgba(0,0,0,0.09)',
                      borderColor: isOnline ? '#38bdf8' : '#f97316'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.75 }}>
                    {/* Header: Site Name + Health Pill */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        {site.siteName}
                      </Typography>

                      <Chip
                        icon={isOnline ? <WifiIcon style={{ fontSize: 13 }} /> : <WifiOffIcon style={{ fontSize: 13 }} />}
                        label={isOnline ? 'Online' : 'Offline'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          bgcolor: isOnline ? '#ecfdf5' : '#fff7ed',
                          color: isOnline ? '#059669' : '#ea580c',
                          border: `1px solid ${isOnline ? '#a7f3d0' : '#ffedd5'}`
                        }}
                      />
                    </Box>

                    {/* Terminal ID */}
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontFamily: 'monospace', fontWeight: 600 }}>
                      {primaryDev?.deviceId || 'No terminal bound'}
                    </Typography>

                    {/* Health & Heartbeat Alert Tag */}
                    <Box sx={{ mt: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: isOnline ? '#10b981' : '#f97316' }} />
                      <Typography variant="caption" sx={{ color: isOnline ? '#475569' : '#c2410c', fontWeight: 600 }}>
                        {isOnline ? 'Active' : 'Offline'} • Last seen: {formatLastSeen(site.lastSeen)}
                      </Typography>
                    </Box>

                    {/* Live Headcount Row */}
                    <Box sx={{ pt: 1.75, pb: 1, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                        Total Present (IN):
                      </Typography>

                      <Chip
                        icon={<PeopleAltIcon style={{ fontSize: 15 }} />}
                        label={`${presentCount} Present`}
                        clickable
                        onClick={() => handleOpenDrilldown(site.siteName)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          px: 1,
                          py: 0.5,
                          bgcolor: presentCount > 0 ? '#eff6ff' : '#f8fafc',
                          color: presentCount > 0 ? '#1d4ed8' : '#64748b',
                          border: `1px solid ${presentCount > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                          '&:hover': { bgcolor: presentCount > 0 ? '#dbeafe' : '#f1f5f9' }
                        }}
                      />
                    </Box>

                    {/* Action Buttons: Force Sync & Pull Live */}
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      {/* Force Sync (Push Historical / All logs to server) */}
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={isForceSyncing || primaryDev?.trigger === 'True'}
                        startIcon={isForceSyncing ? <CircularProgress size={13} /> : <SyncIcon sx={{ fontSize: 15 }} />}
                        onClick={() => handleForceSyncDevice(site.siteName, primaryDev?.deviceId)}
                        sx={{ textTransform: 'none', fontSize: '0.72rem', borderRadius: 1.5, px: 1 }}
                      >
                        {isForceSyncing ? 'Syncing...' : 'Force Sync'}
                      </Button>

                      {/* Pull Live Logs */}
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!isOnline || isPulling}
                        startIcon={isPulling ? <CircularProgress size={13} color="inherit" /> : <CloudDownloadIcon sx={{ fontSize: 15 }} />}
                        onClick={() => handleForcePullSite(site.siteName, primaryDev?.deviceId)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.72rem',
                          borderRadius: 1.5,
                          px: 1.25,
                          bgcolor: '#0284c7',
                          '&:hover': { bgcolor: '#0369a1' }
                        }}
                      >
                        {isPulling ? 'Pulling...' : 'Pull Live'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Drill-Down Attendance Popup Modal */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300, sx: { backdropFilter: 'blur(3px)', bgcolor: 'rgba(15, 23, 42, 0.45)' } } }}
      >
        <Fade in={openModal}>
          <Box sx={modalStyle}>
            {/* Modal Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  📍 {selectedSite} — Live Present Personnel
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Total In-Punches Today: <strong>{(sitePunches[selectedSite] || []).length}</strong> Employees
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Site-Specific Export Button */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportSiteCSV}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    color: '#0f766e',
                    borderColor: '#0f766e',
                    '&:hover': { borderColor: '#115e59', bgcolor: '#f0fdfa' }
                  }}
                >
                  Export Site
                </Button>

                <IconButton onClick={() => setOpenModal(false)} size="small" sx={{ bgcolor: '#f8fafc' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Modal Search Bar */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Employee Code or Name..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Attendance Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, color: '#475569' } }}>
                    <TableCell>Emp Code</TableCell>
                    <TableCell>Employee Name</TableCell>
                    <TableCell>Punch Timestamp</TableCell>
                    <TableCell align="right">Direction</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalFilteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                        No live IN punches recorded for this site yet. Click "Pull Live" on the card.
                      </TableCell>
                    </TableRow>
                  ) : (
                    modalFilteredEmployees.map((record, idx) => {
                      const empDetails = employeeMap[String(record.empCode).trim()];
                      const displayName = record.name || empDetails?.name || 'Staff Member';
                      return (
                        <TableRow key={idx} hover sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' }}>
                            {record.empCode}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {displayName}
                          </TableCell>
                          <TableCell sx={{ color: '#64748b' }}>
                            {record.timestamp}
                          </TableCell>
                          <TableCell align="right">
                            <Chip label="IN" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 700, fontSize: '0.72rem' }} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Dashboard;